import axios from 'axios'
import hash from 'object-hash'
import { omit } from 'ramda'

import { chartPalette } from '../'
import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataBase,
  CompassAPIMultiSeriesChartData,
  getDataSources,
  getMultipleColumnChartProps,
  getMultipleLineChartProps,
  magnitudePointFormatter,
} from '../utils/compass'
import { Palette, datetimeXAxis } from '../utils/constants'
import type { BaseContext } from './'

type SocialMediaType =
  | 'common'
  | 'facebook'
  | 'glassdoor'
  | 'instagram'
  | 'twitter'
  | 'play-store'
  | 'reclame-aqui'
  | 'google-trends'
  | 'similarweb'
  | 'linkedin'

const SOCIAL_MEDIA_URL = '/api/charts/social-media'

interface SocialMediaChartData extends CompassAPIDataBase {
  title: string
  data: {
    series_id: string
    name: string
    values: {
      date: string
      value: number
    }[]
  }[]
}

const convertFormat = (data: SocialMediaChartData) =>
  ({
    ...data,
    data: data.data.map((entry) => ({
      ...omit(['values', 'series_id'], entry),
      data: entry.values,
      id: entry.series_id,
    })),
  } as CompassAPIMultiSeriesChartData)

export namespace SocialMedia {
  export namespace FacebookReactionsCount {
    const CONTEXT_ID = 'equity:social_media:facebook_reactions_count'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      companyId: string
      resampleWindow: string
      minDate: string
      maxDate: string
      reaction: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: SOCIAL_MEDIA_URL + '/facebook/reactions-count-share',
        method: 'POST',
        data: {
          company_id: config.companyId,
          resample_window: config.resampleWindow,
          min_date: config.minDate,
          max_date: config.maxDate,
          reaction: config.reaction,
        },
      }).then((response) => convertFormat(response.data))

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const isShareSeries = (seriesName: string) => seriesName.includes('share')

      const series = data.data?.map((s, idx) => ({
        color: chartPalette[idx],
        data: s.data.map(({ date, value }) => [
          new Date(date).getTime(),
          isShareSeries(s.name) ? value * 100 : value,
        ]),
        id: s.name,
        name: s.name.replaceAll('_', ' '),
        type: 'column',
        tooltip: {
          valueDecimals: 2,
        },
        yAxis: isShareSeries(s.name) ? 'bottom' : 'top',
      }))

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [
        {
          id: 'top',
          opposite: false,
          height: '60%',
          title: { text: 'Count' },
          startOnTick: false,
          endOnTick: false,
        },
        {
          id: 'bottom',
          opposite: false,
          height: '35%',
          top: '65%',
          offset: 0,
          title: { text: 'Share' },
          labels: { format: '{value}%' },
          startOnTick: false,
          endOnTick: false,
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title ?? data.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            tooltip: { shared: true, split: false },
            xAxis: datetimeXAxis,
            series,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace GoogleTrends {
    const CONTEXT_ID = 'equity:social_media:google_trends'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      products: string[]
      chartType: 'monthly-interest' | 'monthly-share'
      isPercentage: boolean
      isColumns: boolean
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: SOCIAL_MEDIA_URL + '/google-trends/' + config.chartType,
        method: 'POST',
        data: {
          product: config.products,
          aggregation_field: 'product',
          aggregation_method: 'sum',
          rolling_window: '30D',
        },
      }).then((response) => convertFormat(response.data))

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data.map((s, idx) => ({
        color: chartPalette[idx],
        data: s.data.map(({ date, value }) => [
          new Date(date).getTime(),
          config.isPercentage ? value * 100 : value,
        ]),
        marker: { enabled: false },
        id: s.name,
        name: s.name.replaceAll('_', ' '),
        type: config.isColumns ? 'column' : 'spline',
        tooltip: {
          valueDecimals: 2,
          xDateFormat: '%A, %b %e, %Y',
        },
        yAxis: 'default',
      }))

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: '' },
          labels: { format: config.isPercentage ? '{value}%' : undefined },
          max: config.isPercentage ? 100 : undefined,
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title ?? data.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            plotOptions: { column: { stacking: 'normal' } },
            tooltip: { shared: true, split: false },
            xAxis: datetimeXAxis,
            series,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace MinCount {
    const CONTEXT_ID = 'equity:social_media:min_count'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    type ChartType =
      | 'company-nps'
      | 'company-peers-nps'
      | 'company-mean-score'
      | 'company-peers-mean-score'

    export interface ChartConfig {
      title?: string
      chartType: ChartType
      socialMediaType: SocialMediaType
      isScoreCount: boolean
      companyId: string
      resampleWindow: string
      minDate: string
      maxDate: string
      minCount: number
      peers: string[]
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url:
          SOCIAL_MEDIA_URL + `/${config.socialMediaType}/${config.chartType}`,
        method: 'POST',
        data: {
          company_id: config.companyId,
          resample_window: config.resampleWindow,
          min_date: config.minDate,
          max_date: config.maxDate,
          min_count: config.minCount,
          peers: config.peers,
        },
      }).then((response) => convertFormat(response.data))

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const DEFAULT_YAXIS = {
        id: 'default',
        opposite: false,
        title: { text: '' },
      }

      const COUNT_YAXIS = {
        id: 'other',
        opposite: false,
        height: '35%',
        top: '65%',
        offset: 0,
        title: { text: 'Count' },
      }

      const series = data.data?.map((s, idx) => ({
        color: chartPalette[idx],
        data: s.data.map(({ date, value }) => [
          new Date(date).getTime(),
          value,
        ]),
        id: s.name,
        name: s.name.replaceAll('_', ' '),
        type: config.isScoreCount && s.name === 'count' ? 'column' : 'spline',
        marker: { enabled: false },
        tooltip: {
          valueDecimals: 2,
        },
        yAxis: config.isScoreCount && s.name === 'count' ? 'other' : 'default',
      }))

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = config.isScoreCount
        ? [
            { ...DEFAULT_YAXIS, height: '60%', title: { text: 'Score' } },
            COUNT_YAXIS,
          ]
        : [DEFAULT_YAXIS]

      return {
        id: Context.getChartId(config),
        title: config.title ?? data.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            plotOptions: { column: { stacking: 'normal' } },
            tooltip: { shared: true, split: false },
            xAxis: datetimeXAxis,
            series,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace PosNeg {
    const CONTEXT_ID = 'equity:social_media:pos_neg'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >
    export interface ChartConfig {
      socialMediaType: SocialMediaType
      title?: string
      companyId: string
      resampleWindow: string
      minDate: string
      maxDate: string
      peers: string[]
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: SOCIAL_MEDIA_URL + `/${config.socialMediaType}/score-count`,
        method: 'POST',
        data: {
          company_id: config.companyId,
          resample_window: config.resampleWindow,
          min_date: config.minDate,
          max_date: config.maxDate,
          peers: config.peers,
        },
      }).then((response) => convertFormat(response.data))

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const SERIES_COLORS: Record<string, keyof typeof Palette> = {
        ratio: 'darkBlue',
        positive: 'darkGreen',
        negative: 'orange',
      }

      const series = data.data?.map((s) => ({
        color: Palette[SERIES_COLORS[s.name]],
        data: s.data.map(({ date, value }) => [
          new Date(date).getTime(),
          value * (s.name === 'negative' ? -1 : 1),
        ]),
        id: s.name,
        name: s.name.replaceAll('_', ' '),
        type: s.name === 'ratio' ? 'spline' : 'column',
        marker: { enabled: false },
        tooltip: {
          valueDecimals: 2,
        },
        yAxis: s.name === 'ratio' ? 'other' : 'default',
      }))

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: 'Revies count' },
          startOnTick: false,
          endOnTick: false,
        },
        {
          id: 'other',
          opposite: true,
          title: { text: 'Reviews ratio' },
          startOnTick: false,
          endOnTick: false,
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title ?? data.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            plotOptions: { column: { stacking: 'normal' } },
            tooltip: { shared: true, split: false },
            xAxis: datetimeXAxis,
            series,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace PostsImpact {
    const CONTEXT_ID = 'equity:social_media:posts_impact'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >
    export interface ChartConfig {
      title?: string
      socialMediaType: SocialMediaType
      companyId: string
      resampleWindow: string
      minDate: string
      maxDate: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: SOCIAL_MEDIA_URL + `/${config.socialMediaType}/posts-impact`,
        method: 'POST',
        data: {
          company_id: config.companyId,
          resample_window: config.resampleWindow,
          min_date: config.minDate,
          max_date: config.maxDate,
        },
      }).then((response) => convertFormat(response.data))

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data.map((s, idx) => ({
        color: chartPalette[idx],
        data: s.data.map(({ date, value }) => [
          new Date(date).getTime(),
          value,
        ]),
        marker: { enabled: false },
        id: s.name,
        name: s.name,
        type: 'column',
        tooltip: {
          valueDecimals: 2,
        },
        yAxis: s.name.includes('like') ? 'top' : 'bottom',
      }))

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [
        {
          id: 'top',
          opposite: false,
          height: '60%',
          title: { text: 'Like' },
          startOnTick: false,
          endOnTick: false,
        },
        {
          id: 'bottom',
          opposite: false,
          height: '35%',
          top: '65%',
          offset: 0,
          title: { text: 'Comments' },
          startOnTick: false,
          endOnTick: false,
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title ?? data.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            tooltip: { shared: true, split: false },
            xAxis: datetimeXAxis,
            series,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace ReactionsInfo {
    const CONTEXT_ID = 'equity:social_media:reactions_info'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    type ChartType =
      | 'reactions-overview-share'
      | 'reactions-overview-count'
      | 'reactions-vs-peers'
      | 'share-vs-peers'
      | 'share-reaction-vs-peers'

    export interface ChartConfig {
      title?: string
      socialMediaType: SocialMediaType
      chartType: ChartType
      companyId: string
      resampleWindow: string
      minDate: string
      maxDate: string
      isColumns: boolean
      isPercentage: boolean
      reaction?: string
      peers: string[]
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url:
          SOCIAL_MEDIA_URL + `/${config.socialMediaType}/${config.chartType}`,
        method: 'POST',
        data: {
          company_id: config.companyId,
          resample_window: config.resampleWindow,
          min_date: config.minDate,
          max_date: config.maxDate,
          reaction: config.reaction,
          peers: config.peers,
        },
      }).then((response) => convertFormat(response.data))

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data.map((s, idx) => ({
        color: chartPalette[idx],
        data: s.data.map(({ date, value }) => [
          new Date(date).getTime(),
          config.isPercentage ? value * 100 : value,
        ]),
        marker: { enabled: false },
        id: s.name,
        name: s.name.replaceAll('_', ' '),
        type: config.isColumns ? 'column' : 'spline',
        tooltip: {
          valueDecimals: 2,
        },
        yAxis: 'default',
      }))

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [
        {
          startOnTick: false,
          endOnTick: false,
          id: 'default',
          opposite: false,
          title: { text: '' },
          labels: { format: config.isPercentage ? '{value}%' : undefined },
          max: config.isPercentage ? 100 : undefined,
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title ?? data.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            plotOptions: { column: { stacking: 'normal' } },
            tooltip: { shared: true, split: false },
            xAxis: datetimeXAxis,
            series,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace ReclameAqui {
    const CONTEXT_ID = 'equity:social_media:reclame_aqui'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    type ChartType =
      | 'solution'
      | 'answers'
      | 'deal-again'
      | 'score'
      | 'quantity'

    export interface ChartConfig {
      title?: string
      chartType: ChartType
      companyId: string
      peers: string[]
      resampleWindow: string
      minDate: string
      maxDate: string
      isPercentage: boolean
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData
    type DataValue = ChartData['data'][number]

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: SOCIAL_MEDIA_URL + `/reclame-aqui/${config.chartType}`,
        method: 'POST',
        data: {
          company_id: [...config.peers, config.companyId],
          resample_window: config.resampleWindow,
          min_date: config.minDate,
          max_date: config.maxDate,
        },
      }).then((response) => convertFormat(response.data))

    const isQuantity = (seriesId: string) => seriesId.slice(0, 2) === 'q_'

    const getLink = (seriesId: string) =>
      isQuantity(seriesId) ? `v_${seriesId.slice(2)}` : undefined

    const sortHelper = (a: DataValue, b: DataValue) =>
      a.name.localeCompare(b.name)

    const getSeriesProps = (
      series: DataValue,
      idx: number,
      company: string | undefined,
      isPercentage: boolean | undefined
    ) => ({
      color: chartPalette[idx % chartPalette.length],
      data: series.data.map(({ date, value }) => [
        new Date(date).getTime(),
        isPercentage ? value * 100 : value,
      ]),
      linkedTo: getLink(series.id!),
      marker: { enabled: false },
      id: series.id!,
      name: series.id!.slice(2),
      type: isQuantity(series.id!) ? 'column' : 'line',
      tooltip: {
        valueDecimals: 2,
        xDateFormat: '%A, %b %e, %Y',
      },
      visible: series.id!.slice(2) === company,
      yAxis: isQuantity(series.id!) ? 'other' : 'default',
    })

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const getSeries = () => {
        if (!data.data) return []
        const quantity = data.data
          .filter(({ id }) => isQuantity(id!))
          .sort(sortHelper)
        const value = data.data
          .filter(({ id }) => !isQuantity(id!))
          .sort(sortHelper)
        return [
          ...quantity.map((series, idx) =>
            getSeriesProps(series, idx, config.companyId, false)
          ),
          ...value.map((series, idx) =>
            getSeriesProps(series, idx, config.companyId, config.isPercentage)
          ),
        ]
      }

      const creditsConfig = getDataSources(data.data_sources)

      const getVerticalLineXAxis = () => {
        if (!data.data) return {}
        const dateInfo = new Date(
          Math.max(
            ...data.data[0].data.map(({ date }) => new Date(date).getTime())
          )
        )
        dateInfo.setMonth(dateInfo.getMonth() - 1)
        return {
          plotLines: [
            {
              color: '#FF0000',
              width: 1,
              value: dateInfo.getTime(),
              dashStyle: 'Dash' as const,
            },
          ],
        }
      }

      const series = getSeries()

      const verticalLineXAxis = getVerticalLineXAxis()

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: 'Valores' },
          labels: { format: config.isPercentage ? '{value}%' : undefined },
          height: '60%',
        },
        {
          id: 'other',
          opposite: false,
          title: { text: 'Quantidade' },
          height: '35%',
          top: '65%',
          offset: 0,
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title ?? data.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            plotOptions: { column: { stacking: 'normal' } },
            tooltip: { shared: true, split: false },
            xAxis: [{ ...verticalLineXAxis, ...datetimeXAxis[0] }],
            series,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace SimilarwebDispersion {
    const CONTEXT_ID = 'equity:social_media:similarweb_dispersion'

    export const __typelist: {
      config: ChartConfig
      data: SimilarwebDispersionChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      SimilarwebDispersionChartData
    >

    export interface ChartConfig {
      title?: string
      products: string[]
      context: typeof CONTEXT_ID
    }

    export interface SimilarwebDispersionChartData extends CompassAPIDataBase {
      title: string
      data: {
        series_id: string
        name: string
        values: {
          x: number
          y: number
          label: string
        }[]
      }[]
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: SOCIAL_MEDIA_URL + '/similarweb/paid-search-bounce-rate',
        method: 'POST',
        data: {
          product: config.products,
        },
      }).then((response) => response.data)

    const getChartProps = (
      config: ChartConfig,
      data: SimilarwebDispersionChartData
    ) => {
      const creditsConfig = getDataSources(data.data_sources)

      const series = data.data.map((s, idx) => ({
        color: chartPalette[idx],
        data: s.values.map(({ x, y, label }) => ({
          x: x * 100,
          y: y * 100,
          name: label,
        })),
        marker: { enabled: true },
        id: s.series_id,
        name: s.name.replaceAll('_', ' '),
        type: 'scatter',
        yAxis: 'default',
        dataLabels: {
          enabled: true,
          format: '{point.name}',
        },
      }))

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: 'Bounce rate' },
          labels: { format: '{value}%' },
        },
      ]
      const xAxis = [
        {
          title: { text: 'Paid search' },
          labels: { format: '{value}%' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title ?? data.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            legend: { enabled: false },
            series,
            tooltip: {
              headerFormat: undefined,
              pointFormat:
                '{point.name}<br><b>Paid search:</b> {point.x:.2f}%<br><b>Bounce Rate:</b> {point.y:.2f}%',
            },
            xAxis,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace SimilarwebTime {
    const CONTEXT_ID = 'equity:social_media:similarweb_time'

    export const __typelist: {
      config: ChartConfig
      data: CompassAPIMultiSeriesChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      CompassAPIMultiSeriesChartData
    >

    type ChartType = 'series' | 'share' | 'ratio' | 'paid-search'

    export interface ChartConfig {
      title?: string
      chartType: ChartType
      resampleWindow: string
      startDate: string
      endDate: string
      isColumns: boolean
      isPercentage: boolean
      fieldNames: string[]
      fieldsDen: string[]
      fieldsNum: string[]
      products: string[]
      context: typeof CONTEXT_ID
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: SOCIAL_MEDIA_URL + `/similarweb/${config.chartType}`,
        method: 'POST',
        data: {
          aggregation_field: 'product',
          aggregation_method: 'sum',
          product: config.products,
          resample_window: config.resampleWindow,
          min_date: config.startDate,
          max_date: config.endDate,
          field_names: config.fieldNames,
          fields_den: config.fieldsDen,
          fields_num: config.fieldsNum,
        },
      }).then((response) => convertFormat(response.data))

    const getChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) => {
      const creditsConfig = getDataSources(data.data_sources)

      const series = data.data.map((s, idx) => ({
        color: chartPalette[idx],
        data: s.data.map(({ date, value }) => [
          new Date(date).getTime(),
          config.isPercentage ? value * 100 : value,
        ]),
        marker: { enabled: false },
        id: s.name,
        name: s.name.replaceAll('_', ' '),
        type: config.isColumns ? 'column' : 'spline',
        tooltip: {
          valueDecimals: 2,
        },
        yAxis: 'default',
      }))

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: '' },
          labels: { format: config.isPercentage ? '{value}%' : undefined },
          max: config.isPercentage ? 100 : undefined,
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title ?? data.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            series,
            tooltip: { shared: false, split: true },
            xAxis: datetimeXAxis,
            yAxis,
            plotOptions: { column: { stacking: 'normal' } },
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace LinkedIn {
    const CONTEXT_ID = 'equity:social_media:linkedin'

    export const __typelist: {
      config: ChartConfig
      data: CompassAPIMultiSeriesChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      CompassAPIMultiSeriesChartData
    >

    type LinkedInChartId =
      | 'total'
      | 'total_peers'
      | 'functional'
      | 'churn'
      | 'churn_peers'
      | 'new_hires'
      | 'new_hires_peers'

    const LINKEDIN_CHART_PATHS: Record<LinkedInChartId, string> = {
      total: '/linkedin/employee-insights-total',
      total_peers: '/linkedin/employee-insights-total-peers',
      functional: '/linkedin/employee-insights-functional',
      churn: '/linkedin/employee-insights-churn',
      churn_peers: '/linkedin/employee-insights-churn-peers',
      new_hires: '/linkedin/employee-insights-new-hires',
      new_hires_peers: '/linkedin/employee-insights-new-hires-peers',
    }

    export interface ChartConfig {
      title?: string
      chart: LinkedInChartId
      minDate: string
      maxDate: string
      companyId: string
      peers: string[]
      context: typeof CONTEXT_ID
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: SOCIAL_MEDIA_URL + LINKEDIN_CHART_PATHS[config.chart],
        method: 'POST',
        data: {
          min_date: config.minDate,
          max_date: config.maxDate,
          company_id: config.companyId,
          peers: config.peers,
        },
      }).then((response) => convertFormat(response.data))

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'functional':
            return getFunctionalChartProps(config, data)
          default:
            return getChartProps(config, data)
        }
      },
    }

    const getFunctionalChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(Context, {
        legend: { maxHeight: 80 },
        plotOptions: { column: { stacking: 'normal' } },
        tooltip: {
          pointFormatter: magnitudePointFormatter(),
          shared: true,
          split: false,
          xDateFormat: '%m/%Y',
        },
        xAxis: {
          labels: { format: '{value:%m/%Y}' },
          tickPositions: data.data[0].data.map((point) =>
            new Date(point.date).getTime()
          ),
        },
      })(config, data)

    const getChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          chartTitle: config.title,
          legend: { maxHeight: 80 },
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            shared: true,
            split: false,
            xDateFormat: '%m/%Y',
          },
          xAxis: { labels: { format: '{value:%m/%Y}' } },
        },
        { type: Array(data.data.length).fill('spline') }
      )(config, data)
  }
}
